import { useState, useMemo, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import {
  Chart as ChartJS, CategoryScale, LinearScale, LineElement, PointElement, Tooltip, Legend, Filler,
} from 'chart.js'
import { Line } from 'react-chartjs-2'
import { getMonthData } from '../api'
import { snapshotPage, popPageSnapshot, restoreScroll } from '../utils/pageState'
import { TransactionsTable } from '../components/txDisplay'
import type { KpiBlock, Transaction } from '../types'

ChartJS.register(CategoryScale, LinearScale, LineElement, PointElement, Tooltip, Legend, Filler)

// ── Helpers ───────────────────────────────────────────────────────────────────
const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
const money  = (v: number) => '$' + Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const numFmt = (v: number) => Math.round(Math.abs(v)).toLocaleString()

const PALETTE = ['#6366f1','#8b5cf6','#ec4899','#f43f5e','#f97316','#f59e0b','#10b981','#06b6d4','#3b82f6','#84cc16']
function catColor(name: string | null | undefined): string {
  if (!name) return '#94a3b8'
  let h = 0
  for (const c of name) h = (h * 31 + c.charCodeAt(0)) & 0x7fffffff
  return PALETTE[h % PALETTE.length]
}

// ── Period / month types ──────────────────────────────────────────────────────
type Period    = 'last12' | number
type CatMetric = 'sum' | 'count' | 'avg'
type ViewMode  = 'charts' | 'calendar'
type CatSortF  = 'name' | 'value' | 'vsLastMo' | 'vsLastYr' | 'rolling' | 'ytd'
type TxSortF   = 'date' | 'merchant' | 'details' | 'category' | 'tag' | 'card' | 'charges'
type SortDir   = 'asc' | 'desc'
interface MonthPill { year: number; month: number; label: string }

const today = new Date()

function computeMonthPills(period: Period): MonthPill[] {
  if (period === 'last12') {
    const pills: MonthPill[] = []
    for (let i = 12; i >= 0; i--) {
      const d  = new Date(today.getFullYear(), today.getMonth() - i, 1)
      const yr = d.getFullYear()
      const mo = d.getMonth() + 1
      pills.push({ year: yr, month: mo, label: MONTH_NAMES[mo - 1] + " '" + String(yr).slice(2) })
    }
    return pills
  }
  const year     = period as number
  const maxMonth = year === today.getFullYear() ? today.getMonth() + 1 : 12
  return Array.from({ length: maxMonth }, (_, i) => ({ year, month: i + 1, label: MONTH_NAMES[i] }))
}

// ── VsBadge ───────────────────────────────────────────────────────────────────
function VsBadge({ value, invert = false }: { value: number | null | undefined; invert?: boolean }) {
  if (value == null) return null
  const isGood = invert ? value >= 0 : value <= 0
  return (
    <span className={isGood ? 'pb-badge-up' : 'pb-badge-down'}>
      <i className={`bi ${value <= 0 ? 'bi-arrow-down' : 'bi-arrow-up'}`} />
      {Math.abs(value).toFixed(1)}%
    </span>
  )
}

// ── Expanded KPI card ─────────────────────────────────────────────────────────
function MonthKpiCard({
  label, kpi, icon, fmt = money, invert = false,
}: {
  label: string; kpi: KpiBlock; icon?: string; fmt?: (v: number) => string; invert?: boolean
}) {
  return (
    <div className="pb-card pb-card-sm h-100">
      <div className="pb-kpi-label mb-1">
        {icon && <i className={`bi ${icon} me-1`} />}{label}
      </div>
      <div className="d-flex align-items-end gap-2 mb-3 flex-wrap">
        <div className="pb-kpi-value">{fmt(kpi.value)}</div>
        <VsBadge value={kpi.vsLastMonth} invert={invert} />
      </div>
      <div style={{ fontSize: '0.78rem', color: 'var(--pb-muted)', display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
        {kpi.lastMonthValue != null && (
          <div className="d-flex justify-content-between align-items-center">
            <span>vs Last Month</span>
            <strong style={{ color: 'var(--pb-text)' }}>{fmt(kpi.lastMonthValue)}</strong>
          </div>
        )}
        {kpi.lastYearValue != null && (
          <div className="d-flex justify-content-between align-items-center gap-2">
            <span style={{ whiteSpace: 'nowrap' }}>vs Last Year</span>
            <span className="d-flex align-items-center gap-1">
              <strong style={{ color: 'var(--pb-text)' }}>{fmt(kpi.lastYearValue)}</strong>
              <VsBadge value={kpi.vsSameMonthLastYear} invert={invert} />
            </span>
          </div>
        )}
        {kpi.rolling12Mo != null && (
          <div className="d-flex justify-content-between align-items-center gap-2">
            <span style={{ whiteSpace: 'nowrap' }}>12-Mo</span>
            <span className="d-flex align-items-center gap-1 flex-wrap justify-content-end">
              <strong style={{ color: 'var(--pb-text)' }}>{fmt(kpi.rolling12Mo)}</strong>
              {kpi.rolling12MoLy != null && (
                <span style={{ color: 'var(--pb-muted)', fontSize: '0.72rem' }}>LY: {fmt(kpi.rolling12MoLy)}</span>
              )}
              <VsBadge value={kpi.vsRolling12Mo} invert={invert} />
            </span>
          </div>
        )}
        {kpi.ytd != null && (
          <div className="d-flex justify-content-between align-items-center gap-2">
            <span>YTD</span>
            <span className="d-flex align-items-center gap-1 flex-wrap justify-content-end">
              <strong style={{ color: 'var(--pb-text)' }}>{fmt(kpi.ytd)}</strong>
              {kpi.ytdLy != null && (
                <span style={{ color: 'var(--pb-muted)', fontSize: '0.72rem' }}>LY: {fmt(kpi.ytdLy)}</span>
              )}
              <VsBadge value={kpi.vsYtd} invert={invert} />
            </span>
          </div>
        )}
      </div>
    </div>
  )
}

// ── Sort header ───────────────────────────────────────────────────────────────
function SortTh({
  label, active, dir, onSort, style,
}: {
  label: string; active: boolean; dir: SortDir; onSort: () => void; style?: React.CSSProperties
}) {
  return (
    <th
      onClick={onSort}
      style={{
        cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap',
        fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase',
        letterSpacing: '0.04em', color: active ? 'var(--pb-primary)' : 'var(--pb-muted)',
        padding: '0.5rem 0.6rem', borderBottom: '2px solid var(--pb-border)',
        transition: 'color 0.15s',
        ...style,
      }}
    >
      {label}
      <i
        className={`bi ms-1 ${active ? (dir === 'asc' ? 'bi-chevron-up' : 'bi-chevron-down') : 'bi-chevron-down'}`}
        style={{ fontSize: '0.65rem', opacity: active ? 1 : 0.3 }}
      />
    </th>
  )
}

// ── Calendar view ─────────────────────────────────────────────────────────────
const PREVIEW_COUNT = 3
// Mon-first day headers
const CAL_DAYS = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun']

function CalendarView({ year, month, transactions, selectedDay, onSelectDay }: {
  year: number
  month: number
  transactions: Transaction[]
  selectedDay: number | null
  onSelectDay: (day: number | null) => void
}) {
  const [expandedDay, setExpandedDay] = useState<number | null>(null)

  // Mon-first: JS getDay() → 0=Sun…6=Sat → Mon-first offset = (getDay()+6)%7
  const rawFirst  = new Date(year, month - 1, 1).getDay()
  const firstDay  = (rawFirst + 6) % 7          // Mon=0, Tue=1…Sun=6
  const daysInMonth = new Date(year, month, 0).getDate()

  const byDay = new Map<number, Transaction[]>()
  for (const t of transactions) {
    if (!t.date) continue
    const part = t.date.split('T')[0].split('-')[2]
    if (!part) continue
    const d = parseInt(part, 10)
    if (!byDay.has(d)) byDay.set(d, [])
    byDay.get(d)!.push(t)
  }

  const cells: (number | null)[] = [
    ...Array.from({ length: firstDay }, () => null as null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ]
  while (cells.length % 7 !== 0) cells.push(null)

  const toggleExpand = (e: React.MouseEvent, day: number) => {
    e.stopPropagation()
    setExpandedDay(prev => prev === day ? null : day)
  }

  const toggleSelect = (day: number) => {
    onSelectDay(selectedDay === day ? null : day)
  }

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 4 }}>
        {CAL_DAYS.map(d => (
          <div key={d} style={{ textAlign: 'center', fontSize: '0.73rem', fontWeight: 600, color: 'var(--pb-muted)', padding: '0.3rem 0 0.4rem' }}>
            {d}
          </div>
        ))}
        {cells.map((day, i) => {
          if (!day) return <div key={`e${i}`} style={{ minHeight: 100, minWidth: 0, borderRadius: 8, border: '1px solid rgba(148,163,184,0.07)' }} />
          const txns     = byDay.get(day) ?? []
          const total    = txns.reduce((s, t) => s + (t.charges ?? 0), 0)
          const isSelected = selectedDay === day
          const isExpanded = expandedDay === day
          const preview    = txns.slice(0, PREVIEW_COUNT)
          const overflow   = txns.length - PREVIEW_COUNT

          return (
            <div
              key={day}
              onClick={() => toggleSelect(day)}
              style={{
                minHeight: 100,
                minWidth: 0,          // allow the 1fr column to constrain this cell
                overflow: 'hidden',   // clip content that overflows the cell
                borderRadius: 8,
                padding: '0.35rem 0.45rem',
                cursor: 'pointer',
                background: isSelected
                  ? 'rgba(99,102,241,0.15)'
                  : txns.length ? 'rgba(99,102,241,0.06)' : 'rgba(148,163,184,0.04)',
                border: isSelected
                  ? '1.5px solid var(--pb-primary)'
                  : '1px solid rgba(148,163,184,0.13)',
                transition: 'background 0.15s, border-color 0.15s',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3, gap: 4 }}>
                <span style={{ fontSize: '0.75rem', fontWeight: 700, flexShrink: 0, color: txns.length ? 'var(--pb-primary)' : 'var(--pb-muted)' }}>
                  {day}
                </span>
                {txns.length > 0 && (
                  <span style={{ fontSize: '0.64rem', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: total < 0 ? 'var(--pb-success)' : '#dc2626' }}>
                    {money(total)}
                  </span>
                )}
              </div>
              {txns.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {(isExpanded ? txns : preview).map((t, ti) => (
                    <div
                      key={ti}
                      title={`${t.merchantDisplayName || t.description || '?'}: ${money(Math.abs(t.charges ?? 0))}`}
                      style={{ display: 'flex', alignItems: 'center', gap: 3, minWidth: 0 }}
                    >
                      <span style={{
                        display: 'inline-block', width: 5, height: 5, borderRadius: '50%', flexShrink: 0,
                        background: (t.charges ?? 0) < 0 ? '#10b981' : catColor(t.category),
                      }} />
                      <span style={{
                        fontSize: '0.61rem', color: 'var(--pb-text)', whiteSpace: 'nowrap',
                        overflow: 'hidden', textOverflow: 'ellipsis', flex: 1, minWidth: 0,
                      }}>
                        {t.merchantDisplayName || t.description || '—'}
                      </span>
                      <span style={{ fontSize: '0.61rem', fontWeight: 600, flexShrink: 0, color: (t.charges ?? 0) < 0 ? 'var(--pb-success)' : '#dc2626', whiteSpace: 'nowrap' }}>
                        {money(Math.abs(t.charges ?? 0))}
                      </span>
                    </div>
                  ))}
                  {overflow > 0 && !isExpanded && (
                    <button
                      onClick={e => toggleExpand(e, day)}
                      style={{ background: 'none', border: 'none', padding: '1px 0', fontSize: '0.61rem', color: 'var(--pb-primary)', cursor: 'pointer', textAlign: 'left', fontWeight: 600 }}
                    >
                      + {overflow} more
                    </button>
                  )}
                  {isExpanded && (
                    <button
                      onClick={e => toggleExpand(e, day)}
                      style={{ background: 'none', border: 'none', padding: '1px 0', fontSize: '0.61rem', color: 'var(--pb-muted)', cursor: 'pointer', textAlign: 'left' }}
                    >
                      ↑ collapse
                    </button>
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ── Main component ────────────────────────────────────────────────────────────
// ── Page-state type (for back-navigation restore) ────────────────────────────
interface MonthPageState {
  period: Period; selYear: number; selMonth: number; viewMode: ViewMode
  catMetric: CatMetric; catSort: CatSortF; catSortDir: SortDir
  txSort: TxSortF; txSortDir: SortDir
  selectedDay: number | null; selectedCat: string | null
}

export default function Month() {
  const navigate = useNavigate()

  const [period,      setPeriod]      = useState<Period>('last12')
  const [selYear,     setSelYear]     = useState(today.getFullYear())
  const [selMonth,    setSelMonth]    = useState(today.getMonth() + 1)
  const [viewMode,    setViewMode]    = useState<ViewMode>('charts')
  const [catMetric,   setCatMetric]   = useState<CatMetric>('sum')
  const [catSort,     setCatSort]     = useState<CatSortF>('value')
  const [catSortDir,  setCatSortDir]  = useState<SortDir>('desc')
  const [txSort,      setTxSort]      = useState<TxSortF>('date')
  const [txSortDir,   setTxSortDir]   = useState<SortDir>('desc')
  const [selectedDay, setSelectedDay] = useState<number | null>(null)
  const [selectedCat, setSelectedCat] = useState<string | null>(null)
  const [pendingScroll, setPendingScroll] = useState<number | null>(null)

  // Restore state when returning from TransactionEdit
  useEffect(() => {
    const snap = popPageSnapshot<MonthPageState>('month')
    if (!snap) return
    const s = snap.state
    setPeriod(s.period)
    setSelYear(s.selYear)
    setSelMonth(s.selMonth)
    setViewMode(s.viewMode)
    setCatMetric(s.catMetric)
    setCatSort(s.catSort)
    setCatSortDir(s.catSortDir)
    setTxSort(s.txSort)
    setTxSortDir(s.txSortDir)
    setSelectedDay(s.selectedDay)
    setSelectedCat(s.selectedCat)
    setPendingScroll(snap.scroll)
  }, [])

  const monthPills = useMemo(() => computeMonthPills(period), [period])

  const { data, isLoading, error } = useQuery({
    queryKey: ['month', selYear, selMonth],
    queryFn:  () => getMonthData(selYear, selMonth),
  })

  const sortedCats = useMemo(() => {
    if (!data) return []
    return [...data.categories].sort((a, b) => {
      const kpiA = catMetric === 'sum' ? a.totalSpent : catMetric === 'count' ? a.transactionCount : a.averageTransaction
      const kpiB = catMetric === 'sum' ? b.totalSpent : catMetric === 'count' ? b.transactionCount : b.averageTransaction
      let av: string | number, bv: string | number
      if      (catSort === 'name')     { av = a.name; bv = b.name }
      else if (catSort === 'value')    { av = kpiA.value; bv = kpiB.value }
      else if (catSort === 'vsLastMo') { av = kpiA.vsLastMonth ?? -Infinity; bv = kpiB.vsLastMonth ?? -Infinity }
      else if (catSort === 'vsLastYr') { av = kpiA.vsSameMonthLastYear ?? -Infinity; bv = kpiB.vsSameMonthLastYear ?? -Infinity }
      else if (catSort === 'rolling')  { av = kpiA.rolling12Mo ?? -Infinity; bv = kpiB.rolling12Mo ?? -Infinity }
      else                             { av = kpiA.ytd ?? -Infinity; bv = kpiB.ytd ?? -Infinity }
      if (typeof av === 'string') {
        const cmp = av.localeCompare(bv as string)
        return catSortDir === 'asc' ? cmp : -cmp
      }
      return catSortDir === 'asc' ? (av as number) - (bv as number) : (bv as number) - (av as number)
    })
  }, [data, catMetric, catSort, catSortDir])

  const sortedTxns = useMemo(() => {
    if (!data) return []
    let txns = [...data.transactions]

    // Filter by selected calendar day
    if (selectedDay != null) {
      txns = txns.filter(t => {
        if (!t.date) return false
        const part = t.date.split('T')[0].split('-')[2]
        return part ? parseInt(part, 10) === selectedDay : false
      })
    }

    // Filter by selected category
    if (selectedCat != null) {
      txns = txns.filter(t => t.category === selectedCat)
    }

    return txns.sort((a, b) => {
      let av: string | number = '', bv: string | number = ''
      if      (txSort === 'date')     { av = a.date ?? '';                  bv = b.date ?? '' }
      else if (txSort === 'merchant') { av = a.merchantDisplayName ?? '';   bv = b.merchantDisplayName ?? '' }
      else if (txSort === 'details')  { av = a.description ?? '';           bv = b.description ?? '' }
      else if (txSort === 'category') { av = a.category ?? '';              bv = b.category ?? '' }
      else if (txSort === 'tag')      { av = a.tag ?? '';                   bv = b.tag ?? '' }
      else if (txSort === 'card')     { av = a.account ?? '';                bv = b.account ?? '' }
      else if (txSort === 'charges')  { av = a.charges ?? 0;                bv = b.charges ?? 0 }
      if (typeof av === 'string') {
        const cmp = (av as string).localeCompare(bv as string)
        return txSortDir === 'asc' ? cmp : -cmp
      }
      return txSortDir === 'asc' ? (av as number) - (bv as number) : (bv as number) - (av as number)
    })
  }, [data, txSort, txSortDir, selectedDay, selectedCat])

  // Early returns after all hooks
  if (isLoading) return (
    <div className="pb-spinner-wrap">
      <div className="spinner-border" style={{ color: 'var(--pb-primary)' }} />
    </div>
  )
  if (error || !data) return (
    <div className="pb-empty"><i className="bi bi-exclamation-triangle me-2" />Failed to load month data.</div>
  )

  // Scroll restoration — runs after data finishes loading
  if (pendingScroll != null && !isLoading) {
    restoreScroll(pendingScroll)
    setPendingScroll(null)
  }

  // ── Handlers ──────────────────────────────────────────────────────────────
  const changePeriod = (p: Period) => {
    setPeriod(p)
    setSelectedDay(null)
    setSelectedCat(null)
    const newPills = computeMonthPills(p)
    if (newPills.length && !newPills.some(mp => mp.year === selYear && mp.month === selMonth)) {
      const last = newPills[newPills.length - 1]
      setSelYear(last.year); setSelMonth(last.month)
    }
  }

  const handleCatSort = (field: CatSortF) => {
    if (catSort === field) setCatSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setCatSort(field); setCatSortDir('desc') }
  }

  const toggleCat = (name: string) => setSelectedCat(prev => prev === name ? null : name)

  // ── Period buttons ─────────────────────────────────────────────────────────
  const earliest = data.earliestYear
  const minYear  = Math.max(earliest, today.getFullYear() - 5)
  const yearRange: number[] = []
  for (let y = today.getFullYear(); y >= minYear; y--) yearRange.push(y)
  const periodBtns: { label: string; value: Period }[] = [
    { label: 'Last 12 Mo', value: 'last12' },
    ...yearRange.map(y => ({ label: String(y), value: y as Period })),
  ]

  // ── Line chart ─────────────────────────────────────────────────────────────
  const prevYrMap  = Object.fromEntries(data.dailyPreviousYear.map(d => [d.day, d.total]))
  const chartLabels = data.dailyCurrentYear.map(d => String(d.day))
  const dailyChart = {
    labels: chartLabels,
    datasets: [
      {
        label: data.monthLabel,
        data: data.dailyCurrentYear.map(d => d.total),
        borderColor: '#6366f1',
        backgroundColor: 'rgba(99,102,241,0.1)',
        fill: true, tension: 0.4,
        pointRadius: 3, pointHoverRadius: 6,
        pointBackgroundColor: '#6366f1',
        borderWidth: 2,
      },
      {
        label: `${selYear - 1} (Prior Year)`,
        data: chartLabels.map(l => prevYrMap[parseInt(l, 10)] ?? 0),
        borderColor: '#94a3b8',
        backgroundColor: 'rgba(148,163,184,0.07)',
        fill: true, tension: 0.4,
        pointRadius: 2, pointHoverRadius: 5,
        pointBackgroundColor: '#94a3b8',
        borderWidth: 1.5,
        borderDash: [5, 4],
      },
    ],
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const chartOpts: any = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right' as const,
        labels: {
          usePointStyle: true,
          pointStyle: 'circle' as const,
          boxWidth: 8,
          font: { size: 11 },
        },
      },
      tooltip: {
        backgroundColor: '#1e293b', padding: 10, cornerRadius: 10,
        callbacks: {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          label: (ctx: any) => (ctx.dataset.label ?? '') + ': ' + money(ctx.raw),
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: '#94a3b8', font: { size: 10 }, maxTicksLimit: 15 },
      },
      y: {
        beginAtZero: true,
        grid: { color: 'rgba(148,163,184,0.15)' },
        border: { display: false },
        ticks: {
          color: '#94a3b8', font: { size: 11 },
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          callback: (v: any) => '$' + (v >= 1000 ? (v / 1000).toFixed(0) + 'k' : v),
        },
      },
    },
  }

  const hasFilters = selectedDay != null || selectedCat != null

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div>
      <h1 className="pb-page-title mb-3">{data.monthLabel}</h1>

      {/* Period selector */}
      <div className="mb-3">
        <div className="pb-chart-selector" style={{ display: 'inline-flex' }}>
          {periodBtns.map(b => (
            <button
              key={String(b.value)}
              className={`pb-chart-sel-btn${period === b.value ? ' active' : ''}`}
              onClick={() => changePeriod(b.value)}
            >
              {b.label}
            </button>
          ))}
        </div>
      </div>

      {/* Month pills */}
      <div className="pb-month-pills mb-4">
        {monthPills.map(p => (
          <button
            key={`${p.year}-${p.month}`}
            className={`pb-month-pill${selYear === p.year && selMonth === p.month ? ' active' : ''}`}
            onClick={() => { setSelYear(p.year); setSelMonth(p.month); setSelectedDay(null); setSelectedCat(null) }}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Charts / Calendar toggle */}
      <div className="mb-4">
        <div className="pb-chart-selector" style={{ display: 'inline-flex' }}>
          <button className={`pb-chart-sel-btn${viewMode === 'charts' ? ' active' : ''}`} onClick={() => setViewMode('charts')}>
            <i className="bi bi-graph-up me-1" />Charts
          </button>
          <button className={`pb-chart-sel-btn${viewMode === 'calendar' ? ' active' : ''}`} onClick={() => setViewMode('calendar')}>
            <i className="bi bi-calendar3 me-1" />Calendar
          </button>
        </div>
      </div>

      {/* KPI cards */}
      <div className="row g-3 mb-4">
        <div className="col-sm-6 col-xl-3"><MonthKpiCard label="Total Earned"    kpi={data.totalEarned}      icon="bi-arrow-down-circle" invert={true} /></div>
        <div className="col-sm-6 col-xl-3"><MonthKpiCard label="Total Spent"     kpi={data.totalSpent}       icon="bi-arrow-up-circle" /></div>
        <div className="col-sm-6 col-xl-3"><MonthKpiCard label="Transactions"    kpi={data.transactionCount} icon="bi-receipt" fmt={numFmt} /></div>
        <div className="col-sm-6 col-xl-3"><MonthKpiCard label="Avg Transaction" kpi={data.averageTransaction} icon="bi-calculator" /></div>
      </div>

      {/* Charts view */}
      {viewMode === 'charts' && (
        <>
          {/* Daily Spending line chart */}
          <div className="pb-card mb-4">
            <div className="pb-kpi-label mb-3">
              <i className="bi bi-graph-up me-2" />Daily Spending — {data.monthLabel}
            </div>
            <div style={{ height: 220 }}>
              <Line data={dailyChart} options={chartOpts} />
            </div>
          </div>

          {/* Category Performance */}
          <div className="pb-card mb-4">
            <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
              <div className="pb-kpi-label"><i className="bi bi-tags me-2" />Category Performance</div>
              <div className="pb-chart-selector" style={{ display: 'inline-flex' }}>
                {([['sum', 'Sum $'], ['count', 'Txn Count'], ['avg', 'Avg $']] as [CatMetric, string][]).map(([m, l]) => (
                  <button key={m} className={`pb-chart-sel-btn${catMetric === m ? ' active' : ''}`} onClick={() => setCatMetric(m)}>{l}</button>
                ))}
              </div>
            </div>
            <div className="pb-table-scroll">
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                <thead>
                  <tr>
                    <SortTh label="Category"   active={catSort === 'name'}     dir={catSortDir} onSort={() => handleCatSort('name')} />
                    <SortTh label="Value"      active={catSort === 'value'}    dir={catSortDir} onSort={() => handleCatSort('value')}    style={{ textAlign: 'right' }} />
                    <SortTh label="vs Last Mo" active={catSort === 'vsLastMo'} dir={catSortDir} onSort={() => handleCatSort('vsLastMo')} style={{ textAlign: 'right' }} />
                    <SortTh label="vs Last Yr" active={catSort === 'vsLastYr'} dir={catSortDir} onSort={() => handleCatSort('vsLastYr')} style={{ textAlign: 'right' }} />
                    <SortTh label="12-Mo"      active={catSort === 'rolling'}  dir={catSortDir} onSort={() => handleCatSort('rolling')}  style={{ textAlign: 'right' }} />
                    <SortTh label="YTD"        active={catSort === 'ytd'}      dir={catSortDir} onSort={() => handleCatSort('ytd')}      style={{ textAlign: 'right' }} />
                  </tr>
                </thead>
                <tbody>
                  {sortedCats.map(cat => {
                    const kpi       = catMetric === 'sum' ? cat.totalSpent : catMetric === 'count' ? cat.transactionCount : cat.averageTransaction
                    const fmt       = catMetric === 'count' ? numFmt : money
                    const color     = catColor(cat.name)
                    const isActive  = selectedCat === cat.name
                    return (
                      <tr
                        key={cat.name}
                        onClick={() => toggleCat(cat.name)}
                        style={{
                          cursor: 'pointer',
                          background: isActive ? `${color}18` : undefined,
                          transition: 'background 0.1s',
                        }}
                        onMouseEnter={e => { if (!isActive) (e.currentTarget as HTMLElement).style.background = 'rgba(99,102,241,0.04)' }}
                        onMouseLeave={e => { if (!isActive) (e.currentTarget as HTMLElement).style.background = '' }}
                      >
                        <td style={{ padding: '0.55rem 0.6rem', borderBottom: '1px solid var(--pb-border)' }}>
                          <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: color, marginRight: '0.5rem', verticalAlign: 'middle' }} />
                          <strong style={{ color: isActive ? 'var(--pb-primary)' : undefined }}>{cat.name}</strong>
                          {isActive && <i className="bi bi-check-circle-fill ms-2" style={{ color: 'var(--pb-primary)', fontSize: '0.75rem' }} />}
                        </td>
                        <td style={{ textAlign: 'right', padding: '0.55rem 0.6rem', borderBottom: '1px solid var(--pb-border)' }}>{fmt(kpi.value)}</td>
                        <td style={{ textAlign: 'right', padding: '0.55rem 0.6rem', borderBottom: '1px solid var(--pb-border)' }}>
                          {kpi.vsLastMonth != null ? <VsBadge value={kpi.vsLastMonth} /> : <span style={{ color: 'var(--pb-muted)' }}>—</span>}
                        </td>
                        <td style={{ textAlign: 'right', padding: '0.55rem 0.6rem', borderBottom: '1px solid var(--pb-border)' }}>
                          {kpi.vsSameMonthLastYear != null ? <VsBadge value={kpi.vsSameMonthLastYear} /> : <span style={{ color: 'var(--pb-muted)' }}>—</span>}
                        </td>
                        <td style={{ textAlign: 'right', padding: '0.55rem 0.6rem', borderBottom: '1px solid var(--pb-border)' }}>
                          {kpi.rolling12Mo != null ? fmt(kpi.rolling12Mo) : <span style={{ color: 'var(--pb-muted)' }}>—</span>}
                        </td>
                        <td style={{ textAlign: 'right', padding: '0.55rem 0.6rem', borderBottom: '1px solid var(--pb-border)' }}>
                          {kpi.ytd != null ? fmt(kpi.ytd) : <span style={{ color: 'var(--pb-muted)' }}>—</span>}
                        </td>
                      </tr>
                    )
                  })}
                  {sortedCats.length === 0 && (
                    <tr><td colSpan={6} className="pb-empty">No categories for this month.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Calendar view */}
      {viewMode === 'calendar' && (
        <div className="pb-card mb-4">
          <div className="pb-kpi-label mb-4">
            <i className="bi bi-calendar3 me-2" />{data.monthLabel}
          </div>
          <CalendarView
            year={selYear}
            month={selMonth}
            transactions={data.transactions}
            selectedDay={selectedDay}
            onSelectDay={setSelectedDay}
          />
        </div>
      )}

      {/* Transactions table */}
      <TransactionsTable
        items={sortedTxns}
        showPB
        stateKey="month"
        resetKey={`${selectedDay ?? ''}|${selectedCat ?? ''}`}
        onRowClick={(id) => {
          snapshotPage<MonthPageState>('month', {
            period, selYear, selMonth, viewMode, catMetric, catSort, catSortDir,
            txSort, txSortDir, selectedDay, selectedCat,
          })
          navigate(`/transactions/${id}/edit`)
        }}
        headerExtra={hasFilters ? (
          <>
            {selectedDay != null && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.8rem', fontWeight: 600, color: 'var(--pb-primary)', background: 'rgba(99,102,241,0.1)', borderRadius: 20, padding: '0.2rem 0.65rem' }}>
                <i className="bi bi-calendar-day" />Day {selectedDay}
                <button onClick={() => setSelectedDay(null)} style={{ background: 'none', border: 'none', padding: 0, color: 'var(--pb-muted)', cursor: 'pointer', lineHeight: 1 }}><i className="bi bi-x" /></button>
              </span>
            )}
            {selectedCat != null && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.8rem', fontWeight: 600, color: 'var(--pb-primary)', background: 'rgba(99,102,241,0.1)', borderRadius: 20, padding: '0.2rem 0.65rem' }}>
                <span style={{ display: 'inline-block', width: 7, height: 7, borderRadius: '50%', background: catColor(selectedCat) }} />{selectedCat}
                <button onClick={() => setSelectedCat(null)} style={{ background: 'none', border: 'none', padding: 0, color: 'var(--pb-muted)', cursor: 'pointer', lineHeight: 1 }}><i className="bi bi-x" /></button>
              </span>
            )}
          </>
        ) : undefined}
      />
    </div>
  )
}
